/**
 * The defect an alert campaign's test call is about (ring R9), in the dialog.
 *
 * Which defect, how serious, when a measurement first saw it and whether a
 * second one confirmed it, and the address the AI would read out. The preview
 * does not recheck the site: the call does, seconds before dialling, and the
 * section says so, because a defect fixed in the meantime refuses the call
 * rather than ringing about something that is no longer true.
 *
 * With no defect worth a call the section is a plain note: the dialog's call
 * button is off for the same reason.
 */
import { AlertTriangle, Mail, RefreshCw } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { alertClassKey, alertClassTone, alertFindingKey } from "../../../lib/aiAlert";
import type { TestCallAlertPreview } from "../../../lib/aiTestCallPreview";
import { StatusPill, fmtDateTime } from "../../ui";

export function AlertEvidence({ alert }: { alert: TestCallAlertPreview }) {
  const t = useT();

  if (alert.status === "none") {
    return (
      <p className="crm-alert">
        <AlertTriangle className="size-4 shrink-0" />
        {t("aiOutbound.testCall.dialog.alert.none")}
      </p>
    );
  }

  const findingKey = alertFindingKey(alert.findingId);
  const classKey = alertClassKey(alert.class);

  return (
    <section className="space-y-1.5">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t("aiOutbound.testCall.dialog.alert.title")}
      </h4>
      <div className="space-y-1.5 rounded-lg border border-border px-3 py-2 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{findingKey ? t(findingKey) : alert.findingId}</span>
          <StatusPill tone={alertClassTone(alert.class)}>{classKey ? t(classKey) : alert.class}</StatusPill>
        </div>
        <p className="crm-sub text-xs">
          {t("aiOutbound.testCall.dialog.alert.firstSeen", { at: fmtDateTime(alert.firstSeenAt) })}
          {" · "}
          {alert.confirmedAt
            ? t("aiOutbound.testCall.dialog.alert.confirmed", { at: fmtDateTime(alert.confirmedAt) })
            : t("aiOutbound.testCall.dialog.alert.unconfirmed")}
        </p>
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <RefreshCw className="size-3.5 shrink-0" />
          {t("aiOutbound.testCall.dialog.alert.recheck")}
        </p>
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Mail className="size-3.5 shrink-0" />
          {alert.email
            ? t("aiOutbound.testCall.dialog.alert.email", { email: alert.email })
            : t("aiOutbound.testCall.dialog.alert.noEmail")}
        </p>
      </div>
    </section>
  );
}
